/**
 * Guard for page-level analytics: decides whether a URL can be a real
 * application route at all.
 *
 * WHY THIS EXISTS
 *
 * On 4–5 Aug 2026 an automated scanner swept the site and produced 453,258
 * `page_viewed` events (32× baseline) against a normal day of ~11k. It
 * requested paths such as `e/action/ListInfo.php` (12,609), `search.php`
 * (3,705), `space.php` (2,351) and `DsxxJsxx.aspx` (3,790).
 *
 * None of those exist here. Gatsby served its 404 page for each one, that page
 * renders inside LayoutProvider like any other, and the tracking effect emitted
 * `page_viewed` with the probe path as `page_name` — so the scanner's URL list
 * ended up in the Amplitude property store.
 *
 * This module is the deterministic half of the fix: a URL that cannot be a
 * route in this app is never tracked, regardless of what the framework decides
 * to render for it. LayoutProvider pairs it with the 404-page marker
 * (`isNotFound`), which catches extension-less probes like `biblio` and
 * `scholar_lookup`.
 *
 * DESIGN CONSTRAINT — false positives are worse than false negatives.
 * Dropping a real page view corrupts reporting silently; letting a probe
 * through only re-creates today's behaviour. Every pattern below is therefore
 * restricted to shapes that provably cannot occur in this app's routing:
 *
 *   pages/app/[...].tsx     -> /app/*      (the SPA splat route)
 *   pages/p/[...].tsx       -> /p/*        (share links, via _redirects)
 *   pages/awards/**, /forms, /thank-you, /subscription, /tv-auth, /deeplink-test
 *   plus the /en /fr /pt locale prefixes from gatsby-plugin-react-i18next
 *
 * No route in that set contains a file extension or a dot-prefixed segment,
 * which is what makes these checks safe.
 */

/**
 * Server-side file extensions. Matched only at the end of a path segment so a
 * legitimate slug that merely contains the letters (e.g. `/awards/php-awards`)
 * is untouched. `php3`/`php5` are included — both appear in scanner wordlists.
 */
const PROBE_EXTENSION =
  /\.(php\d?|phtml|aspx?|jspa?|do|cgi|pl|py|rb|sh|env|git|sql|bak|old|swp|ini|cfg|conf|htaccess)(?:$|[/?#])/i;

/**
 * Dot-prefixed segments (`/.env`, `/.git/config`, `/.aws/credentials`). Gatsby
 * cannot create a page whose path segment begins with a dot, so any request
 * shaped like this is automated by definition.
 */
const DOT_SEGMENT = /(?:^|\/)\.[^/]+/;

/**
 * Upper bound on a sane route. The longest real path here is a locale prefix
 * plus an awards voting UUID (~60 chars); 512 leaves a very wide margin while
 * still rejecting buffer-overflow probes and encoded payloads.
 */
const MAX_PATH_LENGTH = 512;

/**
 * True when the URL is structurally incapable of being a route in this app.
 *
 * Never throws: callers sit on the page-view hot path, and an analytics guard
 * must not be able to break rendering. An unparseable input is treated as a
 * probe, because a real navigation always supplies a usable pathname.
 */
export const isProbePath = (pathname?: string | null): boolean => {
  try {
    if (typeof pathname !== 'string' || pathname.length === 0) return true;
    if (pathname.length > MAX_PATH_LENGTH) return true;

    // Strip query/hash before matching so `/watch?ref=a.php` is not misread as
    // a probe — only the path itself decides.
    const path = pathname.split(/[?#]/)[0];

    if (PROBE_EXTENSION.test(path)) return true;
    if (DOT_SEGMENT.test(path)) return true;

    return false;
  } catch {
    return true;
  }
};

/**
 * Convenience inverse, for call sites that read better as an allow check.
 */
export const isTrackablePath = (pathname?: string | null): boolean =>
  !isProbePath(pathname);

export default isTrackablePath;
